<template>
  <div class="dai-page">
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3" style="position: relative;">
        <div class="dai-top">
          <div>
            <div class="page-titlerow">
              <h2 class="dai-title">Daily</h2>
              <button type="button" class="page-helpbtn" :class="{ 'is-open': helpOpen }" :aria-expanded="helpOpen" @click="helpOpen = !helpOpen">
                <svg class="page-helpbtn__chev" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>How this page works
              </button>
            </div>
            <div class="dai-sub">{{ summaryLine }}</div>
          </div>
        </div>

        <div v-show="helpOpen" class="page-help">
          <ul>
            <li>Everything happening on one day, across every league. A match shows up if it starts today, ends today, or runs straight through today, in your own local time ({{ timezoneLabel }}).</li>
            <li>Matches are ordered by start time. A match that started on an earlier day and is still running gets a blue "Started ..." badge and sorts first, by its own start time.</li>
            <li>An inactive match stays exactly where its start time puts it: it is dimmed, not moved or hidden.</li>
            <li>Click a match to configure its sources (channels) and broadcast events, with the same editors as the Matches and Match Events pages. Once a match has finished it is read-only, and an event that is on air is locked until its match ends.</li>
            <li>A new source starts with the channel on <strong>Automatic</strong> (the system gives it a free one). You can pick a channel by hand from the list instead, and change it later; channels already taken in that time window are marked.</li>
            <li><strong>Order</strong> is the source's position within its match (shown as #1, #2, ...). Leave it empty to use the next free position. If two active sources of a match share an order you get a yellow "same order" warning; it never blocks saving.</li>
            <li>Timing is <strong>Lead</strong> (minutes before the match starts) and <strong>Trail</strong> (minutes after it ends).</li>
          </ul>
        </div>

        <div class="dai-bar">
        <div class="dai-tools">
            <div class="dai-daypick" @click.stop>
            <div class="week-nav">
              <button type="button" class="week-nav-btn" title="Previous day" aria-label="Previous day" @click="goPrevDay"><svg class="wk-chev" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
              <button type="button" class="week-title week-title--btn" :aria-expanded="dayMenuOpen" title="Jump to a nearby day" @click="dayMenuOpen = !dayMenuOpen">
                <svg class="dai-cal-ico" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><rect x="2" y="3" width="12" height="11" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                {{ dayLabelText }}
              </button>
              <button type="button" class="week-nav-btn" title="Next day" aria-label="Next day" @click="goNextDay"><svg class="wk-chev" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
            </div>
            <ul v-if="dayMenuOpen" class="dai-daymenu">
              <li v-for="d in dayChoices" :key="d.param">
                <button
                    type="button"
                    class="dai-daymenu__item"
                    :class="{ 'is-current': d.current, 'is-today': d.today }"
                    @click="pickDay(d.param)"
                >
                  <span>{{ d.label }}</span>
                  <span v-if="d.today" class="dai-daymenu__tag">Today</span>
                </button>
              </li>
            </ul>
            </div>
            <button type="button" class="week-today-btn" :disabled="isToday" @click="goToday">Today</button>
          </div>

        <div class="dai-chips">
          <span class="dai-chip">{{ dailyRows.length }} match{{ dailyRows.length === 1 ? '' : 'es' }}</span>
          <span v-if="carryOverMatches.length" class="dai-chip dai-chip--carry">
            {{ carryOverMatches.length }} from {{ carryOverMatches.length === 1 ? 'an earlier day' : 'earlier days' }}
          </span>
          <span v-if="crossingCount" class="dai-chip">{{ crossingCount }} run{{ crossingCount === 1 ? 's' : '' }} into tomorrow</span>
          <span v-if="inactiveCount" class="dai-chip">{{ inactiveCount }} inactive</span>
          <span v-if="needsSetupCount" class="dai-chip dai-chip--warn">{{ needsSetupCount }} need{{ needsSetupCount === 1 ? 's' : '' }} setup</span>
        </div>
        </div>

        <div class="dai-panels">
          <div class="dai-panel">
            <div class="dai-panel__title">Daily event usage</div>
            <div class="dai-tl-wrap">
              <EventUsageTimeline :groups="eventTimelineGroups" @bar-click="focusMatch"></EventUsageTimeline>
              <div v-if="nowFraction !== null && eventTimelineGroups.length" class="dai-now" :style="{ '--p': nowFraction }"></div>
            </div>
            <details class="dai-legend">
              <summary>How to read this</summary>
              <p>The faded lead-in before a bar is that assignment's own setup time (eventPadSetup) before the match starts; there is no trailing pad. A flat edge means the bar continues past this day's view. A dashed bar is an inactive assignment. Bar color matches the same match in Channel usage, so you can follow one match across both.</p>
            </details>
          </div>

          <div class="dai-panel">
            <div class="dai-panel__title">Daily channels usage<span v-if="channelsWithoutEventCount" class="dai-chip dai-chip--warn ms-2">&#9888; {{ channelsWithoutEventCount }} {{ channelsWithoutEventCount === 1 ? 'source' : 'sources' }} not added to any event</span></div>
            <div class="dai-tl-wrap">
              <ChannelUsageTimeline :rows="channelTimelineRows" @bar-click="focusMatch"></ChannelUsageTimeline>
              <div v-if="nowFraction !== null && channelTimelineRows.length" class="dai-now" :style="{ '--p': nowFraction }"></div>
            </div>
            <details class="dai-legend">
              <summary>How to read this</summary>
              <p>The faded extension behind each bar is that source's own encoder up/down window (match start - lead minutes / match end + trail minutes), usually a little wider than the match. A flat edge means the bar continues past this day's view. A dashed bar is an inactive source, and an amber striped bar is a source that is not added to any event. Click a bar to jump to its match.</p>
            </details>
          </div>
        </div>

        <div v-if="loadError" class="error-message mt-3">{{ loadError }}</div>
        <div v-if="loading" class="dai-muted mt-3">Loading...</div>

        <template v-else>
          <div v-if="!dailyRows.length" class="dai-muted mt-4">No matches touch this day.</div>

          <template v-for="(row, idx) in dailyRows" :key="row.match.calMatchId">
            <div
                v-if="idx === 0 || row.carry !== dailyRows[idx - 1].carry"
                class="dai-group"
                :class="{ 'dai-group--today': !row.carry }"
            >
              {{ row.carry ? 'From earlier days · still on today' : 'Starting today' }}
            </div>

            <div
                :id="'daily-match-' + row.match.calMatchId"
                class="dai-card"
                :class="{ 'dai-card--carry': row.carry, 'dai-card--off': !row.match.active, 'dai-card--done': matchFinished(row.match), 'dai-card--hl': row.match.calMatchId === highlightedMatchId }"
            >
              <div class="dai-card__head" role="button" @click="toggleExpand(row.match.calMatchId)">
                <div class="dai-time">
                  <b>{{ formatLocalTime(row.match.startTime) }} &ndash; {{ formatLocalTime(effectiveEnd(row.match)) }}</b>
                  <small>
                    {{ formatLocalDateShort(row.match.startTime) }}
                    <span v-if="crossesMidnight(row.match)">&rarr; {{ formatLocalDateShort(effectiveEnd(row.match)) }}</span>
                  </small>
                </div>
                <div class="dai-main">
                  <div class="dai-headline">
                    <span v-if="matchPhase(row.match) === 'live'" class="dai-pill dai-pill--live">Live</span>
                    <span v-else-if="matchPhase(row.match) === 'upcoming'" class="dai-pill dai-pill--soon">Upcoming</span>
                    <span v-if="row.match.format" class="dai-pill dai-pill--tag">{{ row.match.format }}</span>
                    <span class="dai-teams">{{ row.match.team1 }} <span class="dai-vs">v</span> {{ row.match.team2 }}</span>
                  </div>
                  <div class="dai-meta">
                    {{ leagueNameFor(row.match.calLeagueId) }}<span v-if="row.match.venue"> &middot; {{ row.match.venue }}</span>
                  </div>
                  <div class="dai-badges">
                    <span v-if="row.carry" class="dai-badge dai-badge--carry">Started {{ formatLocalDateTime(row.match.startTime) }}</span>
                    <span v-if="crossesMidnight(row.match)" class="dai-badge dai-badge--tmr">Ends {{ formatLocalDateTime(effectiveEnd(row.match)) }}</span>
                    <span v-if="!row.match.active" class="dai-badge dai-badge--off">Inactive</span>
                    <span v-if="hasSourceConflict(row.match.calMatchId)" class="dai-badge dai-badge--bad">Source conflict</span>
                  </div>
                </div>
                <div class="dai-chev">{{ isExpanded(row.match.calMatchId) ? '▾' : '▸' }}</div>
              </div>

              <div class="dai-stats" role="button" @click="toggleExpand(row.match.calMatchId)">
                <span>
                  <b>{{ configuredCount(row.match.calMatchId) }}</b> channel{{ configuredCount(row.match.calMatchId) === 1 ? '' : 's' }} configured
                </span>
                <span :class="{ 'dai-zero': totalEventsFor(row.match.calMatchId) === 0 }">
                  <b>{{ totalEventsFor(row.match.calMatchId) }}</b> event{{ totalEventsFor(row.match.calMatchId) === 1 ? '' : 's' }}
                  <template v-if="applicationCountsForMatch(row.match.calMatchId).length">
                    in <b>{{ applicationCountsForMatch(row.match.calMatchId).length }}</b>
                    application{{ applicationCountsForMatch(row.match.calMatchId).length === 1 ? '' : 's' }}
                  </template>
                </span>
                <span v-if="applicationCountsForMatch(row.match.calMatchId).length" class="dai-apps">
                  <template v-for="(ac, i) in applicationCountsForMatch(row.match.calMatchId)" :key="ac.application">{{ i ? ' · ' : '' }}{{ ac.application }}: {{ ac.events }}</template>
                </span>
                <span v-else class="dai-apps dai-apps--warn">Add an event to publish this match</span>
              </div>

              <div class="dai-body" v-show="isExpanded(row.match.calMatchId)">
                <div class="dai-cols">
                  <div class="dai-col">
                    <h4>Sources <span>max 4 per match</span></h4>
                    <div v-if="!matchEditable(row.match) && visibleSlotsFor(row.match).length === 0" class="small text-muted fst-italic">No sources were set up.</div>
                    <CalChannelSlotEditor
                        v-for="slot in visibleSlotsFor(row.match)" :key="slot.key"
                        :source-index="slot.index"
                        :channel="slot.channel"
                        :match-start-time="row.match.startTime"
                        :match-end-time="row.match.endTime"
                        :channel-options="channelOptionsForRow(row.match, slot)"
                        :hardcoded-data="hardcodedData"
                        :channel-conflict="slot.channel && slot.channel.calChannelId ? channelConflicts[slot.channel.calChannelId] : null"
                        :known-channels="calChannelsList"
                        :known-matches="calMatchesList"
                        :show-order="true"
                        :match-channels="channelListByMatch[row.match.calMatchId] || []"
                        :saving="!!matchSlotSaving[row.match.calMatchId + '-' + slot.key]"
                        :error-message="matchSlotErrors[row.match.calMatchId + '-' + slot.key] || ''"
                        :default-lead-minutes="calendarStatic.defaultLeadMinutes"
                        :default-trail-minutes="calendarStatic.defaultTrailMinutes"
                        :language-options="calendarStatic.languages"
                        :locked="isSourceLocked(row.match, slot)"
                        :read-only="!matchEditable(row.match)"
                        @save="(payload) => onMatchSlotSave(row.match.calMatchId, slot.key, payload)"
                        @remove="onSourceRemove(row.match.calMatchId)"
                        @go-to-conflict="onGoToConflict">
                    </CalChannelSlotEditor>
                    <a v-if="matchEditable(row.match) && getVisibleSlotCount(row.match.calMatchId) < 4" role="button" class="dai-link"
                       @click="addSourceSlot(row.match.calMatchId)">
                      + Add source
                    </a>
                  </div>

                  <div class="dai-col">
                    <h4>Events <span>per application</span></h4>
                    <div v-if="!matchEditable(row.match) && visibleEventSlotsFor(row.match).length === 0" class="small text-muted fst-italic">No events were assigned.</div>
                    <MatchEventSlotEditor
                        v-for="slot in visibleEventSlotsFor(row.match)" :key="slot.key"
                        :match-event="slot.matchEvent"
                        :application-list="sportApplicationList"
                        :events-by-application="eventsByApplication"
                        :events-by-id="eventsById"
                        :taken-event-windows="takenEventWindowsForMatch(row.match, slot.matchEvent ? slot.matchEvent.matchEventId : null)"
                        :pad-limits="eventPadLimitsForMatch(row.match)"
                        :read-only="!matchEditable(row.match)"
                        :auto-open-channels="!!(slot.matchEvent && autoOpenChannels[row.match.calMatchId + '-' + slot.matchEvent.eventId])"
                        :saving="!!matchEventSaving[row.match.calMatchId + '-' + slot.key]"
                        :error-message="matchEventErrors[row.match.calMatchId + '-' + slot.key] || ''"
                        :max-pad-setup-minutes="calendarStatic.maxPadSetupMinutes"
                        :channel-options="channelsByMatch[row.match.calMatchId] || []"
                        :linked-channel-ids="slot.matchEvent ? (channelLinksByMatchEvent[slot.matchEvent.matchEventId] || []) : []"
                        :disabled-channel-ids="slot.matchEvent ? channelsClaimedElsewhere(row.match.calMatchId, slot.matchEvent.eventId, slot.matchEvent.matchEventId) : []"
                        :channel-saving="!!(slot.matchEvent && channelLinkSaving[slot.matchEvent.matchEventId])"
                        :channel-error-message="(slot.matchEvent && channelLinkErrors[slot.matchEvent.matchEventId]) || ''"
                        @save="(payload) => onMatchEventSave(row.match.calMatchId, slot.key, payload)"
                        @remove="(payload) => onMatchEventRemove(row.match.calMatchId, slot.key, payload)"
                        @save-channels="(payload) => onSaveChannels(slot.matchEvent && slot.matchEvent.matchEventId, payload)">
                    </MatchEventSlotEditor>
                    <a v-if="matchEditable(row.match)" role="button" class="dai-link" @click="addEventSlot(row.match.calMatchId)">
                      + Add match event
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>

        <div class="dai-foot">
          Times in your local time ({{ timezoneLabel }}).
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminService from "@/services/admin-service";
import XaccelService from "@/services/xaccel-service";
import Header from "@/components/Header.vue";
import { ALL_SPORTS, calendarSport } from "@/utils/calendar-sport";
import CalChannelSlotEditor from "@/components/CalChannelSlotEditor.vue";
import MatchEventSlotEditor from "@/components/MatchEventSlotEditor.vue";
import ChannelUsageTimeline from "@/components/ChannelUsageTimeline.vue";
import EventUsageTimeline from "@/components/EventUsageTimeline.vue";
import { normalizeList, localDateParamFromIso } from "@/utils/general-functions";
import { isValidDateParam, todayLocalDateString } from "./daily/dailyDate";
import dailyDayNav from './daily/dayNav';
import dailySources from './daily/sources';
import dailyEvents from './daily/events';

export default {
  name: "Daily",
  mixins: [dailyDayNav, dailySources, dailyEvents],
  components: {
    Header,
    CalChannelSlotEditor,
    MatchEventSlotEditor,
    ChannelUsageTimeline,
    EventUsageTimeline
  },
  data() {
    return {
      // ?date=YYYY-MM-DD (local day), defaults to today when missing or invalid.
      dateParam: isValidDateParam(this.$route.query.date) ? this.$route.query.date : todayLocalDateString(),
      nowMs: Date.now(),
      helpOpen: false,
      dayMenuOpen: false,
      applicationSports: null,
      calLeaguesList: [],
      calMatchesList: [],
      calChannelsList: [],
      channelConflicts: {},
      channelOptions: [],
      hardcodedData: { encoders: [], origins: [] },
      eventsList: [],
      applicationList: [],
      matchEventsList: [],
      calEventChannelsList: [],
      calendarStatic: {
        languages: [],
        defaultLeadMinutes: 15,
        defaultTrailMinutes: 15,
        maxPadSetupMinutes: 720
      },
      loading: false,
      loadError: '',
      highlightedMatchId: null,
      expandedIds: {},
      visibleSlotCount: {},
      matchSlotSaving: {},
      matchSlotErrors: {},
      visibleEventSlotCount: {},
      matchEventSaving: {},
      // "calMatchId-eventId" of assignments just saved with no channels linked yet - their channel list opens by itself.
      autoOpenChannels: {},
      matchEventErrors: {},
      removedMatchEventIds: {},
      channelLinkSaving: {},
      channelLinkErrors: {}
    };
  },
  computed: {
    // Applications offered in the event editor's dropdown: only those of the header's sport.
    sportApplicationList() {
      return this.applicationList.filter(this.appMatchesSport);
    },
    calLeaguesById() {
      const map = {};
      for (const lg of this.calLeaguesList) map[lg.calLeagueId] = lg;
      return map;
    },
    // Every match that touches this local day, oldest first (includes ones started earlier and still running).
    carryOverMatches() {
      return this.calMatchesList
          .filter(m => this.touchesDay(m) && new Date(m.startTime) < this.dayStart)
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    },
    // Matches that start on this local day, sorted by start time.
    todayStartMatches() {
      return this.calMatchesList
          .filter(m => this.touchesDay(m) && new Date(m.startTime) >= this.dayStart)
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    },
    // Flat, ordered [{ match, carry }] - carry-over matches first, then today's own, each
    // Carry-over matches first, then today's own; each group sorted by start time.
    dailyRows() {
      return [
        ...this.carryOverMatches.map(match => ({ match, carry: true })),
        ...this.todayStartMatches.map(match => ({ match, carry: false }))
      ];
    },
    // Matches that start after this day but whose lead-in (event setup or a source's stream
    // start) already begins inside it. Timeline only - they get no card on this day.
    leadInOnlyMatches() {
      const dayEndMs = this.dayEnd.getTime();
      return this.calMatchesList
          .filter(m => !this.touchesDay(m) && new Date(m.startTime).getTime() >= dayEndMs &&
              this.earliestLeadInMs(m) < dayEndMs)
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    },
    // Every match drawn on the two timelines: today's cards plus the lead-in-only ones.
    timelineMatchIds() {
      return new Set([
        ...this.dailyRows.map(r => r.match.calMatchId),
        ...this.leadInOnlyMatches.map(m => m.calMatchId)
      ]);
    },
    crossingCount() {
      return this.dailyRows.filter(r => this.crossesMidnight(r.match)).length;
    },
    inactiveCount() {
      return this.dailyRows.filter(r => !r.match.active).length;
    },
    needsSetupCount() {
      return this.dailyRows.filter(r => this.totalEventsFor(r.match.calMatchId) === 0).length;
    },
    // { [calMatchId]: 1-8 | null } - fixed color slot per match; null (neutral style) beyond 8 concurrent matches.
    matchColorByMatchId() {
      const map = {};
      const ids = [...this.dailyRows.map(r => r.match.calMatchId), ...this.leadInOnlyMatches.map(m => m.calMatchId)];
      ids.forEach((id, i) => {
        map[id] = i < 8 ? i + 1 : null;
      });
      return map;
    },
    // One row per channelName in use today, sorted by number then base/b/c/d.
    liveNowCount() {
      return this.dailyRows.filter(r => this.matchPhase(r.match) === 'live').length;
    },
    summaryLine() {
      const n = this.dailyRows.length;
      const parts = [`${n} match${n === 1 ? '' : 'es'}`];
      if (this.isToday && this.liveNowCount) parts.push(`${this.liveNowCount} live now`);
      if (this.needsSetupCount) parts.push(`${this.needsSetupCount} need${this.needsSetupCount === 1 ? 's' : ''} setup`);
      return parts.join(' · ');
    },
    nowFraction() {
      if (!this.isToday) return null;
      const f = (this.nowMs - this.dayStart.getTime()) / (24 * 60 * 60 * 1000);
      return f >= 0 && f <= 1 ? f : null;
    },
    // calChannelIds carrying at least one active event assignment (an active link to an active match_events row).
    eventLinkedCalChannelIds() {
      const activeMatchEventIds = new Set(this.matchEventsList.filter(me => me.active).map(me => me.matchEventId));
      const ids = new Set();
      for (const link of this.calEventChannelsList) {
        if (link.active && activeMatchEventIds.has(link.matchEventId)) ids.add(link.calChannelId);
      }
      return ids;
    },
    // Active sources drawn today that no event uses - drives the amber chip on the panel title.
    channelsWithoutEventCount() {
      return this.channelTimelineRows.reduce((n, r) => n + r.bars.filter(b => b.noEvent && b.width > 0).length, 0);
    },
    channelTimelineRows() {
      const touchingIds = this.timelineMatchIds;
      const byChannelName = {};
      for (const ch of this.calChannelsList) {
        if (!touchingIds.has(ch.calMatchId)) continue;
        const match = this.calMatchesList.find(m => m.calMatchId === ch.calMatchId);
        if (!match) continue;
        if (!byChannelName[ch.channelName]) byChannelName[ch.channelName] = [];
        byChannelName[ch.channelName].push({ channel: ch, match });
      }
      return Object.keys(byChannelName)
          .sort((a, b) => this.compareChannelNames(a, b))
          .map(name => {
            const bars = byChannelName[name]
                .slice()
                .sort((a, b) => new Date(a.match.startTime) - new Date(b.match.startTime))
                .map(({ channel, match }) => this.buildTimelineBar(channel, match));
            return { key: name, label: name, warn: bars.some(b => b.noEvent && b.width > 0), bars };
          });
    },
    // One group per known application (even with zero usage), one row per eventId with an active assignment today.
    eventTimelineGroups() {
      const touchingIds = this.timelineMatchIds;
      const byEventId = {};
      for (const me of this.matchEventsList) {
        if (!me.eventId || !me.active || !touchingIds.has(me.calMatchId)) continue;
        const match = this.calMatchesList.find(m => m.calMatchId === me.calMatchId);
        if (!match) continue;
        if (!byEventId[me.eventId]) byEventId[me.eventId] = [];
        byEventId[me.eventId].push({ matchEvent: me, match });
      }
      return this.applicationList.filter(this.appMatchesSport).map(application => {
        const eventIds = (this.eventsByApplication[application] || [])
            .map(ev => ev.eventId)
            .filter(eventId => byEventId[eventId] && byEventId[eventId].length)
            .sort((a, b) => a - b);
        const rows = eventIds.map(eventId => {
          const bars = byEventId[eventId]
              .slice()
              .sort((a, b) => new Date(a.match.startTime) - new Date(b.match.startTime))
              .map(({ matchEvent, match }) => this.buildEventBar(matchEvent, match));
          return {
            key: eventId,
            label: this.eventLabel(eventId),
            warn: bars.some(b => b.noChannels),
            bars
          };
        });
        // Assignments no channel carries yet - drives the amber chip on the application heading.
        const missingCount = rows.reduce((n, r) => n + r.bars.filter(b => b.noChannels).length, 0);
        return { application, missingCount, rows };
      });
    }
  },
  created() {
    this.doLoadAll();
  },
  mounted() {
    this._nowTimer = setInterval(() => { this.nowMs = Date.now(); }, 60 * 1000);
    document.addEventListener('click', this.closeDayMenu);
  },
  beforeUnmount() {
    clearInterval(this._nowTimer);
    document.removeEventListener('click', this.closeDayMenu);
    clearTimeout(this._highlightTimer);
  },
  methods: {
    doLoadAll() {
      this.loading = true;
      this.loadError = '';
      this.loadApplicationList();
      this.loadApplicationSports();
      this.loadCalendarStaticData();
      // Fetched separately so a failure here does not block the rest of the day's data.
      this.loadCalEventChannels();

      const { from, to } = this.fetchWindow();
      Promise.all([
        AdminService.getCalLeaguesList(true),
        AdminService.getAllCalMatches(from, to),
        AdminService.getAllCalChannels(from, to),
        AdminService.getAllMatchEvents(from, to),
        AdminService.getEventsList(true),
        AdminService.getChannelsList(undefined, true),
        XaccelService.getHardcodedData(),
        AdminService.getAllChannelConflicts(from, to)
      ]).then(([leagues, matches, channels, matchEvents, events, channelOptions, hardcodedData, channelConflicts]) => {
        this.calLeaguesList = leagues || [];
        this.calMatchesList = matches || [];
        this.calChannelsList = channels || [];
        this.matchEventsList = matchEvents || [];
        this.eventsList = events || [];
        this.channelOptions = channelOptions || [];
        this.hardcodedData = {
          encoders: normalizeList((hardcodedData || {}).encoders),
          origins: normalizeList((hardcodedData || {}).origins)
        };
        const conflictMap = {};
        (channelConflicts || []).forEach((c) => { conflictMap[c.calChannelId] = c; });
        this.channelConflicts = conflictMap;
        this.applyOpenMatchQuery();
      }).catch((error) => {
        console.error("Error loading daily data:", error);
        this.loadError = this.extractErrorMessage(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    // Wide on purpose, only trims the payload; the precise cut is made client-side.
    fetchWindow() {
      const from = new Date(this.dayStart.getTime() - 24 * 60 * 60 * 1000).toISOString();
      const to = new Date(this.dayEnd.getTime() + 24 * 60 * 60 * 1000).toISOString();
      return { from, to };
    },
    loadApplicationList() {
      const stored = localStorage.getItem('applicationList');
      if (stored) {
        this.applicationList = JSON.parse(stored);
      } else {
        AdminService.getApplications().then(() => {
          this.applicationList = JSON.parse(localStorage.getItem('applicationList') || '[]');
        }).catch((error) => {
          console.error("Error loading application list:", error);
        });
      }
    },
    loadCalendarStaticData() {
      AdminService.getCalendarStaticData().then((data) => {
        this.calendarStatic = data;
      }).catch((error) => {
        console.error("Error loading calendar static data:", error);
      });
    },
    loadCalEventChannels() {
      const { from, to } = this.fetchWindow();
      AdminService.getAllCalEventChannels(from, to).then((links) => {
        this.calEventChannelsList = links || [];
      }).catch((error) => {
        console.error("Error loading cal-event-channel links:", error);
      });
    },
    refetchCalEventChannels() {
      const { from, to } = this.fetchWindow();
      return AdminService.getAllCalEventChannels(from, to).then((links) => {
        this.calEventChannelsList = links || [];
      });
    },
    // Earliest moment a match occupies anything: its start, an active event's setup lead-in,
    // or an active source's feed start (match start - lead minutes) - whichever comes first.
    earliestLeadInMs(match) {
      const start = new Date(match.startTime).getTime();
      let earliest = start;
      for (const me of this.matchEventsByMatch[match.calMatchId] || []) {
        if (!me.eventId || !me.active) continue;
        earliest = Math.min(earliest, start - Math.max(me.eventPadSetup || 0, 0) * 60 * 1000);
      }
      for (const ch of this.channelListByMatch[match.calMatchId] || []) {
        if (!ch.active) continue;
        earliest = Math.min(earliest, start - Math.max(ch.leadMinutes || 0, 0) * 60 * 1000);
      }
      return earliest;
    },
    totalEventsFor(calMatchId) {
      return this.applicationCountsForMatch(calMatchId).reduce((sum, ac) => sum + ac.events, 0);
    },
    // Until the application sports are known (or if that request fails) nothing is hidden.
    appMatchesSport(application) {
      if (calendarSport.value === ALL_SPORTS || !this.applicationSports) return true;
      return this.applicationSports[application] === calendarSport.value;
    },
    loadApplicationSports() {
      AdminService.getApplicationList().then((list) => {
        const map = {};
        for (const app of list) map[app.application] = app.sport;
        this.applicationSports = map;
      }).catch((error) => {
        console.error("Error loading application sports:", error);
      });
    },
    toggleExpand(calMatchId) {
      this.expandedIds = { ...this.expandedIds, [calMatchId]: !this.expandedIds[calMatchId] };
    },
    isExpanded(calMatchId) {
      return !!this.expandedIds[calMatchId];
    },
    focusMatch(calMatchId) {
      if (!this.dailyRows.some(r => r.match.calMatchId === calMatchId)) {
        const match = this.calMatchesList.find(m => m.calMatchId === calMatchId);
        const date = match ? localDateParamFromIso(match.startTime) : undefined;
        if (date) this.$router.push({ name: 'Daily', query: { date, openMatch: String(calMatchId) } });
        return;
      }
      this.expandedIds = { ...this.expandedIds, [calMatchId]: true };
      this.highlightedMatchId = calMatchId;
      clearTimeout(this._highlightTimer);
      this._highlightTimer = setTimeout(() => {
        if (this.highlightedMatchId === calMatchId) this.highlightedMatchId = null;
      }, 4000);
      this.$nextTick(() => {
        const el = document.getElementById('daily-match-' + calMatchId);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
    leagueNameFor(calLeagueId) {
      const lg = this.calLeaguesById[calLeagueId];
      return lg ? lg.name : `League #${calLeagueId}`;
    },
    matchLabel(match) {
      return `Match #${match.calMatchId}, ${this.formatShortRange(match)}`;
    },
    // Arriving with ?openMatch=<calMatchId> (All Matches' "Open", or a go-to-conflict jump
    // from another day/page) - called once the day's data has landed. Silently does nothing
    // if that match doesn't touch this day (stale link, or a deleted match).
    applyOpenMatchQuery() {
      const targetId = Number(this.$route.query.openMatch);
      if (!targetId) return;
      if (this.dailyRows.some(r => r.match.calMatchId === targetId)) {
        this.focusMatch(targetId);
      }
    },
    // Jumps to a conflicted source's other match - if it's on this day, expand+scroll in
    // place; otherwise switch Daily to that match's own (local) day, where
    // applyOpenMatchQuery picks it up. The day is the one the partner match starts on.
    onGoToConflict(conflictMatch) {
      const targetId = conflictMatch.calMatchId;
      if (this.dailyRows.some(r => r.match.calMatchId === targetId)) {
        this.focusMatch(targetId);
        return;
      }
      const date = localDateParamFromIso(conflictMatch.startTime);
      if (!date) return;
      this.$router.push({ name: 'Daily', query: { date, openMatch: String(targetId) } });
    },
    extractErrorMessage(error) {
      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return error.message || 'Something went wrong.';
    }
  }
};
</script>
<style src="../../assets/style.css"></style>
